<!-- filepath: /Users/dineshstrongroom/Desktop/StrongRoom/Code/enterprise-webserver/frontend/src/components/modals/admin-create-edit-recommended-sequence-modal.vue -->
<template>
  <div class="modal-mask">
    <div class="modal-wrapper">
      <div
        class="modal-container"
        style="width: 50%;"
      >
        <!-- HEADER -->
        <div class="modal-header">
          <slot name="header">
            <h3 class="form-header">
              {{ editMode ? "Edit" : "Create" }} Recommended Sequence
            </h3>
            <button
              class="red-close-button"
              @click="closeModal"
            >
              <i class="pi pi-times"></i>
            </button>
          </slot>
        </div>
        <!-- BODY -->
        <div class="modal-body">
          <slot name="body">
            <div class="form-container">
              <div class="flex-input">
                <label for="sequence_title">Title</label>
                <InputText
                  id="sequence_title"
                  v-model="sequenceForm.title"
                  placeholder="Title"
                  class="grey-input"
                  required
                />
              </div>
              <div class="flex-input">
                <label for="sequence_description">Description</label>
                <InputText
                  id="sequence_description"
                  v-model="sequenceForm.description"
                  placeholder="Description"
                  class="grey-input"
                />
              </div>
              <div class="flex-input">
                <label for="sequence_event">Event</label>
                <Dropdown
                  id="sequence_event"
                  v-model="sequenceForm.sequence_event_id"
                  :options="events"
                  option-label="name"
                  option-value="event_id"
                  placeholder="Select Event"
                  class="grey-input"
                  :loading="loadingEvents"
                  required
                />
              </div>
              <div class="flex-input">
                <label for="sequence_templates">Templates</label>
                <MultiSelect
                  id="sequence_templates"
                  v-model="sequenceForm.template_ids"
                  :options="templates"
                  option-label="name"
                  option-value="uuid"
                  placeholder="Select Templates"
                  :loading="loadingTemplates"
                  class="grey-input"
                />
              </div>
              <div class="p-field-checkbox">
                <Checkbox
                  v-model="sequenceForm.marketing_consent"
                  :binary="true"
                  input-id="marketingConsent"
                />
                <label for="marketingConsent">Marketing Consent</label>
              </div>
              <div class="p-field-checkbox">
                <Checkbox
                  v-model="sequenceForm.health_outreach_consent"
                  :binary="true"
                  input-id="healthOutreachConsent"
                />
                <label for="healthOutreachConsent">Health Outreach Consent</label>
              </div>
              <div class="p-field">
                <Button
                  :label="actionButtonText"
                  :loading="loadingCreateSequence"
                  @click="editMode ? editRecommendedSequence() : createRecommendedSequence()"
                />
              </div>
            </div>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AdminCreateEditRecommendedSequenceModal',
  components: {
  },
  props: {
    /**
     * The selected campaigns
     * @type {Object}
     */
    sequence: {
      type: Array,
      default() {
        return []
      }
    },
    /**
     * The confirmation details
     * @type {Object}
     */
     confirmationDetails: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  emits: [
    'closeModal',
    'sequenceCreated',
    'sequenceEdited'
  ],
  data() {
    return {
      sequenceForm: {
        title: '',
        description: '',
        event_id: null,
        template_ids: [],
        marketing_consent: false,
        health_outreach_consent: false,
      },
      events: [],
      loadingEvents: false,
      loadingEventsError: '',
      loadingCreateSequence: false,
      createEditSequenceError: '',
      sequenceCreateEditSuccess: '',
      selectedTemplates: [],
      templates: [],
      loadingTemplates: false,
      loadingTemplatesError: '',
      lazyLoadingTemplates: false,
      currentTemplatesPage: 1,
      totalTemplatePages: 1,
      templateListPerPage: 1,
      searchTemplates: '',
      currentDate: new Date(),
      editMode: false,
      actionButtonText: 'Create',
      displayAddRecommendedTemplatesModal: false,
    }
  },
  computed: {
    /**
     * Create Edit Recommended Sequence Payload
     * @returns {Object}
     */
    createEditRecommendedSequencePayload() {
      let payload = {
        title: this.sequenceForm.title,
        description: this.sequenceForm.description,
        sequence_event_id: this.sequenceForm.sequence_event_id,
        template_ids: this.sequenceForm.template_ids,
        marketing_consent: this.sequenceForm.marketing_consent,
        health_outreach_consent: this.sequenceForm.health_outreach_consent,
      }

      return payload;
    },
    /**
     * Filter the Templates based on search term
     * @returns {Array} - filtered templates
     */
    filteredTemplates() {
      return this.templates.map(template => {
        return {
          template_name: template.template_name,
          template_id: template.template_id
        }
      });
    },
    /**
     * Return the Templates placeholder text
     */
    templatesPlaceholder() {
      return this.loadingTemplates ? 'Loading Templates' : 'Select Templates';
    },
    /**
     * Pharmacy selected items label text
     */
    selectedTemplatesItemsLabel() {
      return this.selectedTemplates && this.selectedTemplates.length ? `${this.selectedTemplates.length} Templates Selected` : 'Select Templates';
    },
    /**
     * Return the max number of selected template labels
     */
    maxSelectedTemplateLabels() {
      if (this.selectedTemplates.length > 0) {
        // If selected templates are not in the list of templates, return 0
        if (this.selectedTemplates.every(selectedTemplate => this.templates.some(template => template.template_id === selectedTemplate.template_id))) {
          return 2;
        }
        else return 0;
      }
      else return 0;
    },
    /**
     * The get templates payload
     * @returns {Object}
     */
    getTemplatesPayload() {
      let payload = {
        page: this.currentTemplatesPage,
        sms_template: true,
        email_template: true,
        global_template: true,
        paginate: true,
      }
      if (this.searchTemplates !== '') {
        payload.search_term = this.searchTemplates;
      }
      return payload;
    },
    /**
     * The events payload
     * @returns {Object}
     */
    getEventsPayload() {
      let payload = {
        page: 1,
      }
      return payload;
    },
  },
  async mounted() {
    this.$store.dispatch('getRefreshToken');
      
    if (this.sequence && this.sequence.sequence_recommendation_id) {
      this.editMode = true;
      this.actionButtonText = 'Update';

      this.sequenceForm.title = this.sequence.title;
      this.sequenceForm.description = this.sequence.description;
      this.sequenceForm.marketing_consent = this.sequence.marketing_consent;
      this.sequenceForm.health_outreach_consent = this.sequence.health_outreach_consent;
    }

    this.loadingTemplates = true;
    this.loadingEvents = true;
    await this.getEvents();
    await this.getTemplates();

    if (this.editMode) {
      this.sequenceForm.sequence_event_id = this.sequence.sequence_event.event_id;
      this.sequenceForm.template_ids = this.sequence.templates.map(template => {
        return template.id;
      });
    }
  },
  methods: {
     /**
     * Get the list of templates for the trial
     */
     async getTemplates() {
      this.loadingTemplates = true;
      this.$store.dispatch('getRefreshToken');
      this.loadingTemplatesError = '';

      if (this.currentTemplatesPage === 1) {
        this.templates = [];
      }

      try {
        const res = await axios.post('/api/template/admin/search', this.getTemplatesPayload, this.$store.state.header);
        this.handleGetTemplatesSuccess(res);
      } catch (err) {
        this.handleGetTemplatesError(this.handleAxiosError(err));
      }
    },
    /**
     * Handle the success response from the get templates endpoint
     * @param {Object} res
     */
    handleGetTemplatesSuccess(res) {
      console.log('The get templates response is: ', res);
      this.templates = res.data.results;
      this.currentTemplatesPage = res.data.current_page;
      this.totalTemplatePages = res.data.page_count;
      this.templateListPerPage = res.data.per_page;
      this.loadingTemplates = false;
    },
    /**
     * Handle the error response from the get templates endpoint
     * @param {Object} err
     */
    handleGetTemplatesError(err) {
      console.log('The get templates error is: ', err);
      this.loadingTemplates = false;
      this.loadingTemplatesError = err;
    },
    /**
     * Update the template list
     */
    async updateTemplateList(event) {
      !this.lazyLoadingTemplates && (this.lazyLoadingTemplates = true);

      //call paginated endpoint
      if (event.last >= (this.templates.length - 3) && this.currentTemplatesPage < this.totalTemplatePages) {
        this.currentTemplatesPage++;
        await this.getTemplates();
      }
      this.lazyLoadingTemplates = false;
    },
    /**
     * Search the template list
     */
    async searchTemplateList(event) {
      !this.lazyLoadingTemplates && (this.lazyLoadingTemplates = true);
      this.searchTemplates = event.value;
      this.currentTemplatesPage = 1;
      this.totalTemplatePages = 1;
      this.templateListPerPage = 1;
      await this.getTemplates();
      this.lazyLoadingTemplates = false;
    },
    /**
     * Get the list of events
     * @returns {Promise<void>}
     */
    async getEvents() {
      this.loadingEvents = true;
      this.$store.dispatch('getRefreshToken');
      this.loadingEventsError = '';

      try {
        const res = await axios.post('/api/sequence-event/admin/list', this.getEventsPayload, this.$store.state.header);
        this.handleGetEventsSuccess(res);
      } catch (err) {
        this.handleGetEventsError(this.handleAxiosError(err));
      }
    },
    /**
     * Handle the success response from the get events endpoint
     * @param {Object} res
     */
    handleGetEventsSuccess(res) {
      console.log('The get events response is: ', res);
      this.events = res.data.sequence_events;
      this.loadingEvents = false;
    },
    /**
     * Handle the error response from the get events endpoint
     * @param {Object} err
     */
    handleGetEventsError(err) {
      console.log('The get events error is: ', err);
      this.loadingEvents = false;
      this.loadingEventsError = err;
    },
   /**
     * Create the recommended sequence
     */
    async createRecommendedSequence() {
      this.loadingCreateSequence = true;
      this.$store.dispatch('getRefreshToken');
      this.createEditSequenceError = '';
      this.sequenceCreateEditSuccess = '';

      try {
        const res = await axios.post('/api/sequence-recommendation/admin/create', this.createEditRecommendedSequencePayload, this.$store.state.header);
        this.handleCreateRecommendedSequenceSuccess(res);
      } catch (err) {
        this.handleCreateRecommendedSequenceError(this.handleAxiosError(err));
      }
    },
    /**
     * Handle the success response from the create recommended sequence endpoint
     * @param {Object} res
     */
    handleCreateRecommendedSequenceSuccess(res) {
      console.log('The create recommended sequence response is: ', res);
      this.loadingCreateSequence = false;
      this.sequenceCreateEditSuccess = 'Recommended Sequence Created Successfully';
      this.$emit('sequenceCreated', res.data);
      this.closeModal();
    },
    /**
     * Handle the error response from the create recommended sequence endpoint
     * @param {Object} err
     */
    handleCreateRecommendedSequenceError(err) {
      console.log('The create recommended sequence error is: ', err);
      this.loadingCreateSequence = false;
      this.createEditSequenceError = err;
      this.sequenceCreateEditSuccess = '';
    },
    /**
     * Edit the recommended sequence
     */
    async editRecommendedSequence() {
      this.loadingCreateSequence = true;
      this.$store.dispatch('getRefreshToken');
      this.createEditSequenceError = '';
      this.sequenceCreateEditSuccess = '';

      try {
        const res = await axios.post('/api/sequence-recommendation/admin/' + this.sequence.sequence_recommendation_id + '/edit', this.createEditRecommendedSequencePayload, this.$store.state.header);
        this.handleEditRecommendedSequenceSuccess(res);
      } catch (err) {
        this.handleEditRecommendedSequenceError(this.handleAxiosError(err));
      }
    },
    /**
     * Handle the success response from the edit recommended sequence endpoint
     * @param {Object} res
     */
    handleEditRecommendedSequenceSuccess(res) {
      console.log('The edit recommended sequence response is: ', res);
      this.loadingCreateSequence = false;
      this.sequenceCreateEditSuccess = 'Recommended Sequence Edited Successfully';
      this.$emit('sequenceEdited', res.data);
      this.closeModal();
    },
    /**
     * Handle the error response from the edit recommended sequence endpoint
     * @param {Object} err
     */
    handleEditRecommendedSequenceError(err) {
      console.log('The edit recommended sequence error is: ', err);
      this.loadingCreateSequence = false;
      this.createEditSequenceError = err;
      this.sequenceCreateEditSuccess = '';
    },
    /**
     * Clear the form
     */
    clear() {
      this.sequenceForm = {
        title: '',
        description: '',
        event_id: null,
        template_ids: [],
        marketing_consent: false,
        health_outreach_consent: false,
      };
      this.selectedTemplates = [];
    },
    /**
     * Close the modal
     */
    closeModal() {
      this.$emit('closeModal');
    },
    /**
     * Close any open modals
     */
    closeOpenModals() {
      this.loadingCreateSequence = false;
      this.displayAddRecommendedTemplatesModal = false;
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../assets/css/main.scss";

.modal-container {
  height: 78vh;
  display: flex;
  flex-direction: column;
}

.form-container {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 20px;
}

.flex-input {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: stretch;
  text-align: left;
}

.flex-input > label {
  font-size: 0.8rem;
}

.p-field-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.form-row {
  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: stretch;

  .span {
    display: flex;
  }
}

.flex-item {
  flex: 1;
}

.radio-group {
  gap: 0.5em;
}

.modal-body {
  padding-left: 40px !important;
  padding-right: 40px !important;
  padding-top: 2.5em;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.modal-header {
  padding: 40px 40px 0 40px !important;
}

.form-header {
  color: black;
}

:deep(.p-accordion-header-link) {
  height: 4em;
  background: white;
  border: none;
  border-top: 1px solid $bordergrey;
}

:deep(.p-accordion-content) {
  border: none;
}

:deep(.p-accordion-content) p {
  margin: 0;
  font-size: 11pt;
  font-weight: 500;
  color: $deepgrey;
  font-family: Montserrat, Helvetica, Arial, sans-serif;
}

:deep(.p-calendar-w-btn .p-datepicker-trigger) {
  background: $strongblue;
}

.error-text {
  color: $error;
  margin: 0 auto 0 0;
  padding-left: 40px;
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  font-weight: 600;
  text-align: left;
}

.red-error-icon {
  min-width: 20px;
  background-color: $error;
  border-color: $error;
  color: white;
  border-radius: 50% !important;
  height: 20px;
  width: 20px;
  margin: auto;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.error-text-span {
  margin: auto;
  padding-left: 10px;
}

.success-message {
  color: $complete;
  margin: 0 auto 0 0;
  padding-left: 40px;
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  font-weight: 600;
  text-align: left;
}

</style>  